import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import InfraNav from './components/InfraNav';
import InfraFooter from './components/InfraFooter';
import InfraCTA from './components/InfraCTA';
import './infrastructure.css';

export default function InfrastructureConnect() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="infra-page" data-testid="infrastructure-connect">
      <InfraNav transparent={false} />

      {/* ══════ HERO ══════ */}
      <div className="infra-product-hero">
        <div className="infra-product-hero-text">
          <div className="infra-product-eyebrow" style={{ color: 'var(--pebbles-orange)' }}>CONNECT</div>
          <h1 className="infra-product-headline">Let third parties build on Pebbles rails.</h1>
          <p className="infra-product-desc">
            Vendors, lenders, insurers, and app developers integrate via API. One platform, full financial services.
          </p>
          <div className="infra-product-buttons">
            <Link to="/login" className="infra-btn-primary" data-testid="connect-get-started">Get started</Link>
            <a href="mailto:hello@pebbles.financial" className="infra-btn-secondary">Contact sales</a>
          </div>
        </div>
        <div className="infra-product-hero-mockup">
          <div className="infra-feature-card" style={{ padding: 0 }}>
            <div className="infra-code-mockup" style={{ margin: 0, borderRadius: 14 }}>
              <pre>
<span className="infra-syn-kw">const</span> <span className="infra-syn-def">pebbles</span> = <span className="infra-syn-fn">require</span>(<span className="infra-syn-str">'pebbles'</span>);{'\n'}{'\n'}<span className="infra-syn-kw">const</span> <span className="infra-syn-def">wallets</span> = <span className="infra-syn-kw">await</span>{'\n'}{'  '}pebbles.wallets<span className="infra-syn-fn">.list</span>({'{'}{'\n'}{'    '}status: <span className="infra-syn-str">'active'</span>,{'\n'}{'    '}limit: <span className="infra-syn-num">20</span>{'\n'}{'  '}{'}'});{'\n'}{'\n'}console<span className="infra-syn-fn">.log</span>(wallets.data.length);{'\n'}<span style={{color:'#6c7086'}}>// 20</span>
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 1: REST API ══════ */}
      <div className="infra-feature">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(216,90,48,0.1)', color: 'var(--pebbles-orange)' }}>API</span>
            <h3 className="infra-feature-title">A complete REST API for financial operations</h3>
            <p className="infra-feature-desc">
              Create wallets, initiate transfers, check balances, manage subscriptions, and verify identities — all through a single, consistent API with predictable resource-based URLs.
            </p>
            <ul className="infra-feature-bullets infra-check-orange">
              <li>RESTful endpoints with JSON responses</li>
              <li>API key and JWT authentication</li>
              <li>Sandbox environment for development</li>
              <li>Rate limiting with clear headers</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pebbles-gray-500)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 8 }}>API Endpoints</div>
                {[
                  { method: 'POST', path: '/v1/wallets', desc: 'Create wallet' },
                  { method: 'GET', path: '/v1/wallets/:id', desc: 'Get wallet' },
                  { method: 'POST', path: '/v1/transfers', desc: 'Initiate transfer' },
                  { method: 'GET', path: '/v1/banks', desc: 'List banks' },
                  { method: 'POST', path: '/v1/banks/resolve', desc: 'Resolve account' },
                ].map((ep, i) => (
                  <div key={i} className="infra-data-row" style={{ borderRadius: 8, border: '1px solid var(--pebbles-gray-100)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, fontFamily: 'monospace', color: ep.method === 'POST' ? 'var(--pebbles-orange)' : 'var(--pebbles-green)', background: ep.method === 'POST' ? 'rgba(216,90,48,0.1)' : 'rgba(29,158,117,0.1)', padding: '2px 6px', borderRadius: 4 }}>{ep.method}</span>
                      <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--pebbles-dark)' }}>{ep.path}</span>
                    </div>
                    <span className="infra-data-label">{ep.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 2: Webhooks ══════ */}
      <div className="infra-feature infra-feature--reverse infra-feature--gray">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(216,90,48,0.1)', color: 'var(--pebbles-orange)' }}>Webhooks</span>
            <h3 className="infra-feature-title">Real-time event notifications</h3>
            <p className="infra-feature-desc">
              Subscribe to events like wallet.credited, transfer.completed, subscription.renewed. We deliver them to your endpoint with automatic retries and signature verification.
            </p>
            <ul className="infra-feature-bullets infra-check-orange">
              <li>20+ event types across all products</li>
              <li>Automatic retry with exponential backoff</li>
              <li>HMAC signature verification</li>
              <li>Event log with replay capability</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pebbles-gray-500)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 4 }}>Recent Events</div>
                {[
                  { event: 'wallet.credited', time: '2s ago', status: 'delivered' },
                  { event: 'transfer.completed', time: '14s ago', status: 'delivered' },
                  { event: 'subscription.renewed', time: '1m ago', status: 'delivered' },
                  { event: 'kyc.verified', time: '3m ago', status: 'delivered' },
                ].map((ev, i) => (
                  <div key={i} className="infra-data-row" style={{ borderRadius: 8, border: '1px solid var(--pebbles-gray-100)' }}>
                    <div>
                      <div style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 500, color: 'var(--pebbles-dark)' }}>{ev.event}</div>
                      <div style={{ fontSize: 9, color: 'var(--pebbles-gray-400)' }}>{ev.time}</div>
                    </div>
                    <span className="infra-badge infra-badge-green">{ev.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 3: SDKs ══════ */}
      <div className="infra-feature">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(216,90,48,0.1)', color: 'var(--pebbles-orange)' }}>SDKs</span>
            <h3 className="infra-feature-title">Client libraries for every stack</h3>
            <p className="infra-feature-desc">
              Official SDKs for Python, JavaScript, and PHP. Type-safe, well-documented, and maintained. Or use the REST API directly — your choice.
            </p>
            <ul className="infra-feature-bullets infra-check-orange">
              <li>Python, JavaScript, PHP libraries</li>
              <li>Type-safe with full IDE support</li>
              <li>Automatic pagination handling</li>
              <li>Built-in error handling and retries</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { lang: 'JavaScript', pkg: 'npm install @pebbles/sdk', color: '#f0db4f' },
                  { lang: 'Python', pkg: 'pip install pebbles-sdk', color: '#3776AB' },
                  { lang: 'PHP', pkg: 'composer require pebbles/sdk', color: '#777BB4' },
                ].map((sdk, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', borderRadius: 8, padding: '10px 12px', border: '1px solid var(--pebbles-gray-100)' }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: `${sdk.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: sdk.color }}>{sdk.lang[0]}</span>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--pebbles-dark)' }}>{sdk.lang}</div>
                      <div style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--pebbles-gray-400)' }}>{sdk.pkg}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 4: Marketplace ══════ */}
      <div className="infra-feature infra-feature--reverse infra-feature--gray">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(216,90,48,0.1)', color: 'var(--pebbles-orange)' }}>Marketplace</span>
            <h3 className="infra-feature-title">A marketplace of financial services</h3>
            <p className="infra-feature-desc">
              Vendors, lenders, and insurers list their products. Employees discover and subscribe. Collections happen automatically at payroll. Everyone wins.
            </p>
            <ul className="infra-feature-bullets infra-check-orange">
              <li>Vendor onboarding and verification</li>
              <li>Product listing and discovery</li>
              <li>Automatic payroll-level collection</li>
              <li>Revenue sharing and settlement</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pebbles-gray-500)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 4 }}>Marketplace Partners</div>
                {[
                  { name: 'GDL+', cat: 'Investment', users: '342 subscribers' },
                  { name: 'AXA Mansard', cat: 'Insurance', users: '215 subscribers' },
                  { name: 'FitLife Gym', cat: 'Wellness', users: '89 subscribers' },
                  { name: 'Korapay', cat: 'Payments', users: '156 subscribers' },
                ].map((p, i) => (
                  <div key={i} className="infra-data-row" style={{ borderRadius: 8, border: '1px solid var(--pebbles-gray-100)' }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--pebbles-dark)' }}>{p.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--pebbles-gray-400)' }}>{p.cat}</div>
                    </div>
                    <span style={{ fontSize: 10, color: 'var(--pebbles-gray-500)' }}>{p.users}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <InfraCTA />
      <InfraFooter />
    </div>
  );
}
